import React from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    Button,
    Typography,
    CircularProgress
} from "@material-ui/core";
import { useApiCache } from "customHooks/useApi";
import { useAuthState } from "_ReactContext/AuthContext";
import { DialogHeader } from "./components/DialogHeader";
import { ProgressSection } from "./components/ProgressSection";
import { StagesSection } from "./components/StagesSection";
import { useDialogStyles } from "./styles/dialogStyles";
import { TFunction } from "i18next";
import { Alert } from "@material-ui/lab";

interface JobApprovalStatusDialogProps {
    open: boolean;
    onClose: () => void;
    jobId: string;
    jobTitle: string;

    t: TFunction;
}

export const jobApprovalStatusQueryAddress =
    "/jobapprovalworkflow/get-job-approval-status";

export function JobApprovalStatusDialog({
    open,
    onClose,
    jobId,
    jobTitle,
    t
}: JobApprovalStatusDialogProps) {
    const classes = useDialogStyles();
    const { company } = useAuthState();

    const [getJobApprovalReturn] = useApiCache(
        jobApprovalStatusQueryAddress,
        "post",
        {
            jobId,
            companyId: company.companyId
        },
        false,
        1000 * 60 * 5
    );

    const isLoading = getJobApprovalReturn.status !== "success";
    const data: Compleo.JobApproval.jobApprovalStatus =
        getJobApprovalReturn?.response?.data?.data;
    const jobStatus: CompleoShared.Common.JobStatus = data?.status;

    const getProgressPercentage = (): number => {
        if (
            jobStatus === "jobApprovalRejected" ||
            jobStatus === "jobApprovalExpired" ||
            jobStatus === "jobApprovalApproved"
        ) {
            return 100;
        }
        if (!data.totalStages || !data.currentStage) {
            return 0;
        }
        let totalNumberOfApproversRequired = 0;
        let totalNumberOfApprovals = 0;
        for (const stage of data.stagesStatus || []) {
            const stageType = stage.approvalLogic?.value;
            const totalApprovalsStage = stage.approverStatus.filter(
                (a) => a.actionResult?.status === "approved"
            ).length;
            totalNumberOfApprovals += totalApprovalsStage;
            if (stageType === "or") {
                totalNumberOfApproversRequired += 1;
            } else if (stageType === "and") {
                totalNumberOfApproversRequired +=
                    (stage.externalApprovers || []).length +
                    (stage.internalApprovers || []).length;
            }
        }
        // return Math.round(((data.currentStage - 1) / data.totalStages) * 100);
        return totalNumberOfApproversRequired > 0
            ? Math.round(
                  (totalNumberOfApprovals / totalNumberOfApproversRequired) *
                      100
              )
            : 0;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="job-approval-dialog-title"
            maxWidth="lg"
            fullWidth
            scroll="paper"
        >
            <DialogContent
                className={classes.dialogContent}
                style={{ margin: 0, padding: 0 }}
            >
                {isLoading ? (
                    <div className={classes.loadingContainer}>
                        <CircularProgress />
                    </div>
                ) : data ? (
                    <>
                        <DialogHeader
                            jobId={jobId}
                            jobTitle={jobTitle}
                            currentStage={data.currentStage}
                            totalStages={data.totalStages}
                            t={t}
                            jobStatus={jobStatus}
                        />

                        {data.totalStages && data.currentStage && (
                            <ProgressSection
                                currentStage={data.currentStage}
                                totalStages={data.totalStages}
                                progressPercentage={getProgressPercentage()}
                                t={t}
                            />
                        )}

                        <StagesSection
                            stages={data.stagesStatus || []}
                            t={t}
                            jobStatus={jobStatus}
                        />
                    </>
                ) : (
                    <Alert severity="info" style={{ margin: 20 }}>
                        {t("jobWithoutApprovalWorkflow")}
                    </Alert>
                )}
            </DialogContent>

            <DialogActions className={classes.footer}>
                <Button onClick={onClose} color="primary" variant="contained">
                    {t("COMPLEOGENERAL_CLOSE")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
